import * as React from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Input, Select } from "@jhool-io/fe-components";
import { useProcessPaymentLinkPayment } from "../../hooks/mutations/payment-link.mutations";
import {
    IProcessPaymentLinkPaymentPayload,
    PaymentLinkType,
} from "../../types/payment-link.types";
import { STATES } from "../../../../utils/helpers/us-states/us-states";
import { showMoneyInAppFormat } from "../../../../utils/helpers";
import useToast from "../../../../hooks/useToast";
import {
    isAcceptJSValidationError,
    useAcceptJS,
} from "../../../../hooks/useAcceptJS/useAcceptJS";

const schema = yup.object({
    card_number: yup.string().required("Card number is required"),
    card_cvv: yup
        .string()
        .min(3, "CVV must be at least 3 digits")
        .max(4, "CVV cannot be longer than 4 digits")
        .matches(/^\d+$/, "CVV must contain only numbers")
        .required("Card CVV is required"),
    expiration_date: yup
        .string()
        .required("Expiration date is required")
        .matches(
            /^(0[1-9]|1[0-2])\/\d{2}$/,
            "Expiration date must be in MM/YY format (e.g., 12/25)"
        ),
    first_name: yup.string().required("First name is required"),
    last_name: yup.string().required("Last name is required"),
    billing_address: yup.string().required("Billing address is required"),
    state: yup.string().required("State is required"),
    zip_code: yup.string().required("Zip code is required"),
    country: yup.string().required("Country is required"),
});

interface PaymentLinkPaymentFormProps {
    paymentLinkRef: string;
    totalAmount: number;
    linkType?: PaymentLinkType | null;
    verificationCode?: string | null;
    onFormSubmit: () => void;
}

interface PaymentFormData {
    card_number: string;
    card_cvv: string;
    expiration_date: string;
    first_name: string;
    last_name: string;
    billing_address: string;
    state: string;
    zip_code: string;
    country: string;
}

type Option = {
    label: React.ReactNode;
    value: string;
};

export default function PaymentLinkPaymentForm({
    paymentLinkRef,
    totalAmount,
    linkType,
    verificationCode,
    onFormSubmit,
}: PaymentLinkPaymentFormProps) {
    const {
        control,
        register,
        handleSubmit,
        setError,
        formState: { errors },
    } = useForm<PaymentFormData>({
        resolver: yupResolver(schema),
        mode: "onChange",
        defaultValues: {
            country: "USA",
        },
    });

    const { mutate } = useProcessPaymentLinkPayment();
    const { toast } = useToast();

    const getStateSelectOptions = STATES.map((state) => ({
        label: state.name,
        value: state.abbrv,
    }));

    const { tokenizeCard } = useAcceptJS();

    const setAcceptJSCardErrors = (error: unknown) => {
        if (!isAcceptJSValidationError(error)) {
            return;
        }

        error.issues.forEach((issue) => {
            switch (issue.field) {
                case "card_number":
                    setError("card_number", {
                        type: "validate",
                        message: issue.message,
                    });
                    break;

                case "expiration_date":
                    setError("expiration_date", {
                        type: "validate",
                        message: issue.message,
                    });
                    break;

                case "card_cvv":
                    setError("card_cvv", {
                        type: "validate",
                        message: issue.message,
                    });
                    break;

                case "zip_code":
                    setError("zip_code", {
                        type: "validate",
                        message: issue.message,
                    });
                    break;

                case "cardholder_name":
                    setError("first_name", {
                        type: "validate",
                        message: issue.message,
                    });
                    setError("last_name", {
                        type: "validate",
                        message: issue.message,
                    });
                    break;

                default:
                    toast({ mode: "error", message: issue.message });
            }
        });
    };

    const onSubmit = async (payload: PaymentFormData) => {
        const isCodedLink =
            linkType === PaymentLinkType.CLOSED || Boolean(verificationCode);

        const codeToUse = isCodedLink ? verificationCode : undefined;

        let tokenResult: Awaited<ReturnType<typeof tokenizeCard>>;

        try {
            const [expirationMonth = "", expirationYear = ""] =
                payload.expiration_date.split("/");

            tokenResult = await tokenizeCard({
                cardNumber: payload.card_number,
                cardCode: payload.card_cvv,
                month: expirationMonth,
                year: expirationYear ? `20${expirationYear}` : "",
                fullName: `${payload.first_name} ${payload.last_name}`,
                zip: payload.zip_code,
            });
        } catch (error) {
            setAcceptJSCardErrors(error);
            return;
        }

        const { opaqueData } = tokenResult;

        const dataToSend: IProcessPaymentLinkPaymentPayload = {
            ref: paymentLinkRef,
            tokenized_payment: {
                opaque_data: {
                    dataDescriptor: opaqueData.dataDescriptor,
                    dataValue: opaqueData.dataValue,
                },
                billing_info: {
                    first_name: payload.first_name,
                    last_name: payload.last_name,
                    billing_address: payload.billing_address,
                    state: payload.state,
                    zip_code: payload.zip_code,
                    country: payload.country,
                },
            },
            ...(codeToUse ? { code: codeToUse } : {}),
        };

        mutate(dataToSend, {
            onSuccess: (res) => {
                toast({
                    mode: "success",
                    message: res.message || "Payment processed successfully",
                });
                onFormSubmit();
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Payment failed. Please try again.",
                });
            },
        });
    };

    return (
        <>
            <div className="text-center mb-6">
                <p className="text-base font-medium mb-2 text-gray">
                    Total Amount:{" "}
                    <span className="text-textLink font-semibold">
                        {showMoneyInAppFormat(totalAmount)}
                    </span>
                </p>
                <p className="text-xs text-danger">
                    Note: Your card details are not saved and will not be
                    stored.
                </p>
            </div>

            <form
                id="payment-link-payment-form"
                onSubmit={handleSubmit(onSubmit)}
            >
                <div className="fg fg-space-between two flex">
                    <Input
                        {...register("first_name")}
                        label="First Name"
                        placeholder="First Name"
                        hasError={!!errors.first_name}
                        errorText={errors.first_name?.message}
                        className="fs-exclude"
                        isRequired
                    />
                    <Input
                        {...register("last_name")}
                        label="Last Name"
                        placeholder="Last Name"
                        hasError={!!errors.last_name}
                        errorText={errors.last_name?.message}
                        className="fs-exclude"
                        isRequired
                    />
                </div>
                <div className="fg">
                    <Input
                        {...register("card_number")}
                        label="Card Number"
                        placeholder="Card Number"
                        hasError={!!errors.card_number}
                        errorText={errors.card_number?.message}
                        className="fs-exclude"
                        isRequired
                    />
                </div>
                <div className="fg fg-space-between two flex">
                    <Input
                        {...register("expiration_date")}
                        maxLength={5}
                        label="Expiration Date (MM/YY)"
                        placeholder="MM/YY"
                        hasError={!!errors.expiration_date}
                        errorText={errors.expiration_date?.message}
                        className="fs-exclude"
                        isRequired
                    />
                    <Input
                        {...register("card_cvv")}
                        label="Security Code (CVV)"
                        placeholder="CVV"
                        hasError={!!errors.card_cvv}
                        errorText={errors.card_cvv?.message}
                        className="fs-exclude"
                        isRequired
                    />
                </div>
                <div className="fg">
                    <Input
                        {...register("billing_address")}
                        label="Billing Address"
                        placeholder="Billing Address"
                        hasError={!!errors.billing_address}
                        errorText={errors.billing_address?.message}
                        className="fs-exclude"
                        isRequired
                    />
                </div>
                <div className="fg">
                    <Input
                        {...register("zip_code")}
                        label="Zip Code"
                        placeholder="Zip Code"
                        hasError={!!errors.zip_code}
                        errorText={errors.zip_code?.message}
                        isRequired
                    />
                </div>
                <div className="fg fg-space-between two flex">
                    <Controller
                        name="state"
                        control={control}
                        render={({ field }) => (
                            <Select
                                label="State"
                                options={getStateSelectOptions}
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                isSearchable
                                isLongListInDialog
                                hasError={!!errors.state}
                                errorText={errors.state?.message}
                                isRequired
                            />
                        )}
                    />
                    <Input
                        {...register("country")}
                        label="Country"
                        placeholder="Country"
                        hasError={!!errors.country}
                        errorText={errors.country?.message}
                        isRequired
                    />
                </div>
            </form>
        </>
    );
}
